import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './CoursePage.css';

const CoursePage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await fetch(`https://mv-profile-959685280830.asia-south1.run.app/courses-and-modules/${courseId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch course');
        }
        const data = await response.json();
        setCourse(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      fetchCourse();
    }
  }, [courseId]);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  if (loading) return (
    <div className="course-loading">
      <div className="loading-spinner"></div>
      <p>Loading course content...</p>
    </div>
  );
  
  if (error) return (
    <div className="course-error">
      <h2>Oops! Something went wrong</h2>
      <p>{error}</p>
      <button onClick={() => window.location.reload()} className="retry-btn">Try Again</button>
    </div>
  );
  
  if (!course) return (
    <div className="course-not-found">
      <h2>Course Not Found</h2>
      <p>The course you're looking for doesn't exist or may have been removed.</p>
    </div>
  );

  return (
    <div className="course-container">
      
      <div className="course-hero">
        <div className="course-hero-content">
          <div className="course-info-main">
            <div className="breadcrumb">Development • Programming Languages • Python</div>
            <h1>{course.title}</h1>
            <p className="course-description">{course.description}</p>
            
            <div className="course-rating">
              <span className="rating-value">4.7</span>
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="star">★</span>
                ))}
              </div>
              <span className="rating-count">(1,234 ratings)</span>
              <span className="student-count">5,678 students</span>
            </div>
            
            <div className="course-meta">
              <span>Created by <strong>{course.instructor}</strong></span>
              <span>Last updated {course.updatedDate}</span>
              <span>{course.language}</span>
            </div>
          </div>
          
          <div className="course-purchase-card">
            <div className="course-image">
              <img 
                src={`https://source.unsplash.com/400x225/?${course.category.toLowerCase()},learning`} 
                alt={course.title}
              />
              <div className="preview-overlay">Preview this course</div>
            </div>
            
            <div className="pricing">
              <div className="price-container">
                <span className="current-price">${course.price}</span>
                <span className="original-price">$94.99</span>
                <span className="discount">42% off</span>
              </div>
              
              <div className="timer">1 day left at this price!</div>
            </div>
            
            <div className="purchase-actions">
              <button className="add-to-cart-btn">Add to cart</button>
              <button className="buy-now-btn">Buy now</button>
            </div>
            
            <div className="money-back">
              30-Day Money-Back Guarantee
            </div>
            
            <div className="includes-list">
              <h4>This course includes:</h4>
              <ul>
                <li>10 hours on-demand video</li>
                <li>15 articles</li>
                <li>20 downloadable resources</li>
                <li>Full lifetime access</li>
                <li>Certificate of completion</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      
      <div className="course-content-container">
        <div className="course-content">
          <div className="content-header">
            <h2>Course content</h2>
            <div className="content-stats">
              <span>{course.modules.length} sections • 45 lectures • 10h 15m total length</span>
            </div>
          </div>
          
          <div className="modules-accordion">
            {course.modules
              .sort((a, b) => a.order - b.order)
              .map((module, index) => (
                <div key={module.moduleId} className="module-item">
                  <div 
                    className="module-header"
                    onClick={() => toggleModule(module.moduleId)}
                  >
                    <div className="module-title">
                      <span className="module-number">Section {index + 1}:</span>
                      <h3>{module.title}</h3>
                    </div>
                    <div className="module-info">
                      <span>5 lectures • 45m</span>
                      <span className={`toggle-icon ${expandedModules[module.moduleId] ? 'expanded' : ''}`}>
                        {expandedModules[module.moduleId] ? '▲' : '▼'}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`module-content ${expandedModules[module.moduleId] ? 'expanded' : ''}`}>
                    {module.submodules
                      .sort((a, b) => a.order - b.order)
                      .map((submodule) => (
                        <div key={submodule.subModuleId} className="submodule-item">
                          <div className="submodule-header">
                            <div className="lecture-icon">▶</div>
                            <div className="submodule-info">
                              <h4>{submodule.title}</h4>
                              <p className="submodule-description">{submodule.description}</p>
                            </div>
                            <div className="lecture-duration">10:25</div>
                          </div>
                          
                          <div className="videos-container">
                            {submodule.coursevideo[0]?.video
                              .sort((a, b) => a.order - b.order)
                              .map((video, index) => (
                                <div key={index} className="video-item">
                                  <div className="video-header">
                                    <h5>Video {video.order}: {video.videoUrl ? video.videoUrl.split('/').pop() : 'No Video'}</h5>
                                    <span className="video-duration">15:30</span>
                                  </div>
                                  {video.videoUrl ? (
                                    <div className="video-player-container">
                                      <video 
                                        controls 
                                        className="video-player"
                                        poster={`https://source.unsplash.com/600x340/?${course.category.toLowerCase()}`}
                                        src={`https://your-video-host.com/videos/${video.videoUrl}`}
                                      >
                                        Your browser does not support the video tag.
                                      </video>
                                    </div>
                                  ) : (
                                    <p className="no-video">Video not uploaded yet</p>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
        
        <div className="course-sidebar">
          <div className="what-youll-learn">
            <h3>What you'll learn</h3>
            <ul>
              <li>Master Python programming fundamentals</li>
              <li>Build real-world applications</li>
              <li>Work with databases and APIs</li>
              <li>Develop problem-solving skills</li>
              <li>Prepare for technical interviews</li>
            </ul>
          </div>
          
          <div className="requirements">
            <h3>Requirements</h3>
            <ul>
              <li>Basic computer skills</li>
              <li>No prior programming experience needed</li>
              <li>Willingness to learn</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Instructor Section */}
      <div className="instructor-section">
        <h2>Instructor</h2>
        <div className="instructor-profile">
          <div className="instructor-image">
            <img src="https://source.unsplash.com/100x100/?portrait,professional" alt={course.instructor} />
          </div>
          <div className="instructor-details">
            <h3>{course.instructor}</h3>
            <p className="instructor-title">Senior Developer and Educator</p>
            <div className="instructor-stats">
              <div className="stat">
                <span className="stat-value">4.7</span>
                <span className="stat-label">Instructor Rating</span>
              </div>
              <div className="stat">
                <span className="stat-value">12,345</span>
                <span className="stat-label">Reviews</span>
              </div>
              <div className="stat">
                <span className="stat-value">56,789</span>
                <span className="stat-label">Students</span>
              </div>
              <div className="stat">
                <span className="stat-value">15</span>
                <span className="stat-label">Courses</span>
              </div>
            </div>
            <p className="instructor-bio">
              With over 10 years of experience in software development and teaching, 
              {course.instructor} has helped thousands of students master programming 
              concepts and advance their careers. Their practical approach to teaching 
              focuses on real-world applications and problem-solving.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePage;